import React, { useEffect, useState } from 'react';

/**
 * Real-time FPS read-only badge — polls engine.getFps() every 500ms and
 * color-codes the value:
 *   • ≥ 60 fps → green (smooth)
 *   • 30–60   → amber (medium)
 *   • < 30    → red (heavy)
 *
 * Read-only: never mutates engine, scene, or projection state. If the engine
 * is not yet created (initial app load) the component renders nothing.
 */
const FpsMeter = () => {
  const [fps, setFps] = useState(null);

  useEffect(() => {
    let raf = null;
    const tick = () => {
      const engine = window.__babylonEngine;
      if (engine && typeof engine.getFps === 'function') {
        const value = engine.getFps();
        setFps(Number.isFinite(value) ? Math.round(value) : null);
      }
    };
    // 500 ms cadence — frequent enough to feel "live" without spamming
    // re-renders that themselves cost frames.
    const interval = setInterval(tick, 500);
    tick();
    return () => {
      clearInterval(interval);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  if (fps === null) return null;

  let colorClass = 'text-green-400';
  if (fps < 30) colorClass = 'text-red-400';
  else if (fps < 60) colorClass = 'text-amber-400';

  return (
    <div
      className="flex items-center gap-1 text-xs font-mono tabular-nums"
      data-testid="fps-meter"
      title={`Real-time FPS · ${fps >= 60 ? 'Smooth' : fps >= 30 ? 'Medium' : 'Heavy'}`}
    >
      <span className="text-zinc-500">FPS</span>
      <span className={`${colorClass} font-semibold`}>{fps}</span>
    </div>
  );
};

export default FpsMeter;
